import { Box, Checkbox, Divider, Skeleton, Stack } from '@mui/material';

// Thin one-line rows, used for the things an app's trust is made of: its file set imports and the
// base policies its artifacts are built for. Read-only on a card, checkable inside a form.

export const COMPACT_ROW_SX = {
  px: 1,
  py: 0.4,
  minWidth: 0,
  cursor: 'pointer',
  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.03)' },
};

export const COMPACT_ROW_NAME_SX = {
  color: '#FFFFFF',
  fontWeight: 600,
  fontSize: '0.74rem',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

// The identifier a row carries (a hash, a policy id), which takes whatever width is left.
export const COMPACT_ROW_ID_SX = {
  fontFamily: 'monospace',
  fontSize: '0.66rem',
  color: '#D1D5DB',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
  flex: 1,
};

export const COMPACT_ROW_META_SX = {
  color: '#9CA3AF',
  fontSize: '0.68rem',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

// The text that opens what a row describes, when the row itself does something else.
export const COMPACT_ROW_LINK_SX = {
  cursor: 'pointer',
  '&:hover': { textDecoration: 'underline' },
};

// Fixed width so the pipes line up whichever word sits beside them.
export const COMPACT_ROW_STATUS_LABEL_SX = {
  fontSize: '0.68rem',
  fontWeight: 600,
  width: 48,
  textAlign: 'left',
};

// An unselected row keeps its layout and loses a little contrast, rather than fading out as a whole.
export const MUTED_TEXT_COLOR = '#8E949C';
export const MUTED_ICON_OPACITY = 0.6;

export const CompactRowList = ({ children }) => (
  <Stack
    divider={<Divider sx={{ borderColor: '#5B5B5B' }} />}
    sx={{ border: '1px solid #5B5B5B', borderRadius: '6px', overflow: 'hidden' }}>
    {children}
  </Stack>
);

// Sits between a row and what acts on it at its end.
export const RowPipe = () => (
  <Divider orientation="vertical" flexItem sx={{ borderColor: '#6B7280', my: 0.25 }} />
);

// Inside a form the list is drawn like the outlined fields around it, its title set into the border.
const OUTLINE_COLOR = 'rgba(255, 255, 255, 0.23)';

export const OUTLINED_ROW_DIVIDER = <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />;

export const OutlinedRowList = ({ label, adornment, children }) => (
  <Box
    component="fieldset"
    sx={{ m: 0, p: 0, minWidth: 0, border: `1px solid ${OUTLINE_COLOR}`, borderRadius: 1 }}>
    <Box
      component="legend"
      sx={{
        ml: 1,
        px: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        fontSize: '0.75rem',
        lineHeight: 1,
        color: 'text.secondary',
      }}>
      {label}
      {adornment}
    </Box>
    {children}
  </Box>
);

export const OutlinedRowListSkeleton = () => (
  <Stack gap={0.5} sx={{ p: 1 }}>
    <Skeleton variant="rounded" height={26} sx={{ bgcolor: '#4B4B4B' }} />
    <Skeleton variant="rounded" height={26} sx={{ bgcolor: '#4B4B4B' }} />
  </Stack>
);

const CHECKBOX_SX = {
  p: 0.25,
  flexShrink: 0,
  color: '#9CA3AF',
  '&.Mui-checked': { color: '#87C8E0' },
  '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.3)' },
};

const stopPropagation = event => event.stopPropagation();

// The row's own click toggles the box too, so the box only stops its click reaching the row.
export const RowCheckbox = ({ checked, disabled, onChange, label }) => (
  <>
    <RowPipe />
    <Checkbox
      size="small"
      checked={checked}
      disabled={disabled}
      onClick={stopPropagation}
      onChange={onChange}
      inputProps={{ 'aria-label': label }}
      sx={CHECKBOX_SX}
    />
  </>
);
